import type { Task } from "@/api/tasks";
import { conversationFrameTaskId } from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { presentArtifactBesideConversation } from "@/lib/conversation-artifact-presentation";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import type { PresentationState } from "@/lib/focus/focus-types";
import { useFramesStore, type FramesState } from "@/stores/frames-store";
import { selectFramesByType } from "@/stores/frames-selectors";

/** The frame a task shows beside a focused board: its conversation, newest deliverable, or source note. */
export function taskFocusFrameId(
  task: Pick<Task, "id" | "deliverable_frame_ids" | "source" | "frame">,
  frames: Pick<FramesState, "framesById" | "frameOrder">,
): string | null {
  const conversation = selectFramesByType(frames, "conversation").find(
    (frame) => conversationFrameTaskId(frame) === task.id,
  );
  if (conversation) return conversation.frameId;
  const deliverableIds = new Set(task.deliverable_frame_ids ?? []);
  const newest = frames.frameOrder
    .filter((id) => deliverableIds.has(id))
    .map((id) => frames.framesById[id]!)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
  if (newest) return newest.frameId;
  const sourceNote =
    task.source === "note" && !!task.frame && !!frames.framesById[task.frame];
  return sourceNote ? task.frame : null;
}

export function isFrameInFocus(
  frameId: string,
  presentation: PresentationState | null,
): boolean {
  return (
    presentation?.variant === "focus" && presentation.frameIds.includes(frameId)
  );
}

/**
 * Show a task's frame as a tile beside the focused board. A conversation takes
 * the chat column with the board beside it, because plain focus tiles never mix
 * chats with other frames and would drop the board.
 */
export function showTaskFocusTile(boardFrameId: string, tileFrameId: string) {
  const tile = useFramesStore.getState().framesById[tileFrameId];
  if (tile?.frameType === "conversation") {
    presentArtifactBesideConversation(tileFrameId, boardFrameId);
    return;
  }
  activateFrameInCurrentContext(tileFrameId);
}
